import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Search, BarChart3, Info, Loader2, Plug, RefreshCw, Trash2, MousePointerClick, Eye,
} from "lucide-react";

const PROVIDERS = {
    gsc: { label: "Google Search Console", Icon: Search },
    ga4: { label: "Google Analytics 4", Icon: BarChart3 },
};

// Google sends people back here after they approve access.
export const GOOGLE_REDIRECT_PATH = "/seo";
const redirectUri = () => `${window.location.origin}${GOOGLE_REDIRECT_PATH}`;

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

/** Finishes the Google sign-in when the page loads with ?code&state. */
export function useGoogleOAuthReturn(businessId, onDone) {
    const handled = useRef(false);
    useEffect(() => {
        if (!businessId || handled.current) return;
        const q = new URLSearchParams(window.location.search);
        const code = q.get("code");
        const state = q.get("state");
        const err = q.get("error");
        if (!state || (!code && !err)) return;
        handled.current = true;
        window.history.replaceState({}, "", window.location.pathname + window.location.hash);
        if (err) {
            toast.info(err === "access_denied" ? "Google connection cancelled." : `Google: ${err}`);
            return;
        }
        (async () => {
            const t = toast.loading("Connecting Google…");
            try {
                const r = await api.googleExchangeCode(businessId, { code, state });
                toast.dismiss(t);
                if (r.result?.ok) {
                    toast.success(`${PROVIDERS[r.provider]?.label || "Google"} connected.`);
                } else {
                    toast.error(`Google connection failed: ${r.result?.error || "unknown error"}`);
                }
                onDone?.();
            } catch (e) {
                toast.dismiss(t);
                const d = e?.response?.data?.detail;
                toast.error(typeof d === "string" ? d : "Google connection failed.");
            }
        })();
    }, [businessId, onDone]);
}

export default function GoogleConnections({ google, businessId, onChange }) {
    const [busy, setBusy] = useState(null);
    const [insights, setInsights] = useState(null);
    const anyLive = ["gsc", "ga4"].some((p) => google?.[p]?.status === "connected");

    useEffect(() => {
        if (!businessId || !anyLive) {
            setInsights(null);
            return;
        }
        let alive = true;
        api.googleInsights(businessId)
            .then((r) => alive && setInsights(r))
            .catch(() => alive && setInsights(null));
        return () => {
            alive = false;
        };
    }, [businessId, anyLive, google?.gsc?.site_url, google?.ga4?.property_id]);

    const act = async (key, fn, ok) => {
        setBusy(key);
        try {
            await fn();
            if (ok) toast.success(ok);
            onChange?.();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Something went wrong.");
        } finally {
            setBusy(null);
        }
    };

    const connect = (prov) =>
        act(`connect-${prov}`, async () => {
            const r = await api.googleOauthUrl(businessId, prov, redirectUri());
            if (!r.configured) {
                const err = new Error(r.message);
                err.response = { data: { detail: r.message } };
                throw err;
            }
            window.location.assign(r.url);
        });

    return (
        <div className="space-y-3" data-testid="google-connections">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {["gsc", "ga4"].map((prov) => {
                    const s = google?.[prov] || {};
                    const { label, Icon } = PROVIDERS[prov];
                    const connected = s.status === "connected";
                    const errored = s.status === "error";
                    const options =
                        prov === "gsc"
                            ? (s.sites || []).map((v) => ({ v, l: v }))
                            : (s.properties || []).map((p) => ({ v: p.id, l: `${p.name}${p.account ? ` · ${p.account}` : ""}` }));
                    const current = prov === "gsc" ? s.site_url : s.property_id;
                    return (
                        <div
                            key={prov}
                            data-testid={`google-panel-${prov}`}
                            className="surface rounded-2xl p-4 flex items-start gap-3"
                        >
                            <div
                                className={`h-8 w-8 rounded-xl border flex items-center justify-center shrink-0 ${connected ? "border-emerald-500/30 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}
                            >
                                <Icon className={`h-4 w-4 ${connected ? "text-emerald-300" : "text-slate-400"}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-semibold text-slate-100">{label}</span>
                                    <Badge
                                        variant="outline"
                                        data-testid={`google-status-${prov}`}
                                        className={
                                            connected
                                                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                                                : errored
                                                  ? "border-rose-500/30 bg-rose-500/10 text-rose-200"
                                                  : "border-white/10 text-slate-400"
                                        }
                                    >
                                        {connected ? "Live" : errored ? "Needs attention" : "Not connected"}
                                    </Badge>
                                </div>
                                <div className="text-[11px] text-slate-400 mt-1 truncate">
                                    {connected ? s.account_label : s.honesty_label || "Not connected"}
                                </div>
                                {connected && options.length > 1 && (
                                    <select
                                        value={current || ""}
                                        onChange={(e) =>
                                            act(`select-${prov}`, () => api.googleSelect(businessId, prov, e.target.value), "Saved.")
                                        }
                                        data-testid={`google-select-${prov}`}
                                        className="mt-2 w-full rounded-lg bg-white/[0.03] border border-white/10 text-xs text-slate-200 h-8 px-2"
                                    >
                                        {options.map((o) => (
                                            <option key={o.v} value={o.v} className="bg-slate-900">
                                                {o.l}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                {!google?.configured && (
                                    <div className="mt-2 text-[10px] text-amber-200 flex items-start gap-1.5">
                                        <Info className="h-3 w-3 mt-0.5 shrink-0" />
                                        Google sign-in isn't set up on the server yet (GOOGLE_OAUTH_CLIENT_ID,
                                        GOOGLE_OAUTH_CLIENT_SECRET).
                                    </div>
                                )}
                                {s.verification_error && (
                                    <div className="mt-2 text-[10px] text-rose-200">Google reported: {s.verification_error}</div>
                                )}
                                <div className="mt-3 flex gap-1.5 flex-wrap">
                                    {!connected ? (
                                        <Button
                                            size="sm"
                                            onClick={() => connect(prov)}
                                            disabled={!google?.configured || busy === `connect-${prov}`}
                                            data-testid={`google-connect-${prov}`}
                                            className="h-8 bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5"
                                        >
                                            {busy === `connect-${prov}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
                                            {errored ? "Connect again" : "Connect"}
                                        </Button>
                                    ) : (
                                        <>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => act(`verify-${prov}`, () => api.googleVerify(businessId, prov), "Connection checked.")}
                                                disabled={busy === `verify-${prov}`}
                                                data-testid={`google-verify-${prov}`}
                                                className="h-8 border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5"
                                            >
                                                {busy === `verify-${prov}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                                Check
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => act(`disc-${prov}`, () => api.googleDisconnect(businessId, prov), "Disconnected.")}
                                                disabled={busy === `disc-${prov}`}
                                                data-testid={`google-disconnect-${prov}`}
                                                className="h-8 border-rose-500/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 gap-1.5"
                                            >
                                                <Trash2 className="h-3 w-3" /> Disconnect
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {insights && (insights.gsc || insights.ga4) && <GoogleInsights data={insights} />}
        </div>
    );
}

function Stat({ label, value, Icon }) {
    return (
        <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
            <div className="label-eyebrow flex items-center gap-1.5">
                {Icon && <Icon className="h-3 w-3" />} {label}
            </div>
            <div className="mt-1 text-xl font-extrabold text-slate-50">{value}</div>
        </div>
    );
}

function GoogleInsights({ data }) {
    const { gsc, ga4 } = data;
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="google-insights">
            {gsc && (
                <div className="surface rounded-2xl p-4">
                    <div className="flex items-center justify-between gap-2">
                        <div className="label-eyebrow text-emerald-300">Search Console · last 28 days</div>
                        {gsc.site_url && <span className="text-[10px] mono text-slate-500 truncate">{gsc.site_url}</span>}
                    </div>
                    {gsc.error ? (
                        <div className="mt-2 text-xs text-rose-200">{gsc.error}</div>
                    ) : (
                        <>
                            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <Stat label="Clicks" value={fmt(gsc.clicks)} Icon={MousePointerClick} />
                                <Stat label="Impressions" value={fmt(gsc.impressions)} Icon={Eye} />
                                <Stat label="CTR" value={`${gsc.ctr}%`} />
                                <Stat label="Avg position" value={gsc.position || "—"} />
                            </div>
                            {gsc.top_queries?.length > 0 && (
                                <table className="mt-3 w-full text-xs">
                                    <thead>
                                        <tr className="text-slate-500 text-left">
                                            <th className="font-medium py-1">Top searches</th>
                                            <th className="font-medium py-1 text-right">Clicks</th>
                                            <th className="font-medium py-1 text-right">Impr.</th>
                                            <th className="font-medium py-1 text-right">Pos.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gsc.top_queries.map((q) => (
                                            <tr key={q.key} className="border-t border-white/5 text-slate-300">
                                                <td className="py-1 pr-2 truncate max-w-[180px]">{q.key}</td>
                                                <td className="py-1 text-right">{fmt(q.clicks)}</td>
                                                <td className="py-1 text-right">{fmt(q.impressions)}</td>
                                                <td className="py-1 text-right">{q.position}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}
                </div>
            )}
            {ga4 && (
                <div className="surface rounded-2xl p-4">
                    <div className="flex items-center justify-between gap-2">
                        <div className="label-eyebrow text-cyan-300">Google Analytics · last 28 days</div>
                        {ga4.property_name && <span className="text-[10px] mono text-slate-500 truncate">{ga4.property_name}</span>}
                    </div>
                    {ga4.error ? (
                        <div className="mt-2 text-xs text-rose-200">{ga4.error}</div>
                    ) : (
                        <>
                            <div className="mt-3 grid grid-cols-3 gap-2">
                                <Stat label="Sessions" value={fmt(ga4.sessions)} />
                                <Stat label="Visitors" value={fmt(ga4.users)} />
                                <Stat label="Engaged" value={`${ga4.engagement_rate}%`} />
                            </div>
                            {ga4.channels?.length > 0 && (
                                <div className="mt-3 space-y-1.5">
                                    <div className="text-[11px] text-slate-500">Where visitors came from</div>
                                    {ga4.channels.map((c) => {
                                        const pct = ga4.sessions ? Math.round((c.sessions / ga4.sessions) * 100) : 0;
                                        return (
                                            <div key={c.channel} className="flex items-center gap-2 text-xs text-slate-300">
                                                <span className="w-32 truncate">{c.channel}</span>
                                                <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                                    <div className="h-full bg-cyan-400" style={{ width: `${pct}%` }} />
                                                </div>
                                                <span className="w-14 text-right mono">{fmt(c.sessions)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
